import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { postsApi, api } from '../api/client';
import {
  BookOpen, MessageSquareQuote, HeartHandshake, Sparkles, HandHeart, Heart,
  MoreHorizontal, Pencil, Trash2, Flag, X, Users,
  MessageCircle, CheckCircle2, ChevronLeft, ChevronRight,
  Hash, Bookmark, BookmarkCheck, Repeat2, Sunrise,
} from 'lucide-react';
import { useTranslation } from '../i18n';
import { useAuth } from '../context/AuthContext';
import ReportModal from './ReportModal';
import { renderShareCard } from './PostShareCard';
import ReactionsSheet from './ReactionsSheet';
import CommentSection from './CommentSection';
import { shortName } from '../utils/displayName';
import '../screens/FeedScreen.css';

const TYPE_META = {
  scripture:      { Icon: BookOpen,           bannerClass: 'scripture',      taglineKey: 'scriptureTagline'      },
  reflection:     { Icon: MessageSquareQuote, bannerClass: 'reflection',     taglineKey: 'reflectionTagline'     },
  prayer:         { Icon: HeartHandshake,     bannerClass: 'prayer',         taglineKey: 'prayerTagline'         },
  praise:         { Icon: Sparkles,           bannerClass: 'praise',         taglineKey: 'praiseTagline'         },
  surrender:      { Icon: HandHeart,          bannerClass: 'surrender',      taglineKey: 'surrenderTagline'      },
  prayer_request: { Icon: Heart,              bannerClass: 'prayer_request', taglineKey: 'prayerRequestTagline'  },
};
// Keep TYPE_ICONS as alias so any other screen importing it still works
const TYPE_ICONS = {
  scripture:  { bg: '#E6F7EE', text: '#1A5D60', Icon: BookOpen           },
  reflection: { bg: '#EDF2E4', text: '#3D5228', Icon: MessageSquareQuote },
  prayer:     { bg: '#EAE3F7', text: '#5B36B0', Icon: HeartHandshake     },
  praise:     { bg: '#FBF1C0', text: '#A06500', Icon: Sparkles           },
  surrender:  { bg: '#DDD6FE', text: '#4C1D95', Icon: HandHeart          },
};

// ── Structured content parsing ─────────────────────────────────────────────────
function parsePostContent(text) {
  const markers = {};
  let clean = text;

  const scriptureMatch = clean.match(/\[SCRIPTURE\]([\s\S]*?)\[\/SCRIPTURE\]/);
  if (scriptureMatch) {
    try { markers.scripture = JSON.parse(scriptureMatch[1]); } catch {}
    clean = clean.replace(scriptureMatch[0], '').trim();
  }
  const praisingMatch = clean.match(/\[PRAISING\]([\s\S]*?)\[\/PRAISING\]/);
  if (praisingMatch) {
    try { markers.praising = JSON.parse(praisingMatch[1]); } catch {}
    clean = clean.replace(praisingMatch[0], '').trim();
  }
  const surrenderingMatch = clean.match(/\[SURRENDERING\]([\s\S]*?)\[\/SURRENDERING\]/);
  if (surrenderingMatch) {
    try { markers.surrendering = JSON.parse(surrenderingMatch[1]); } catch {}
    clean = clean.replace(surrenderingMatch[0], '').trim();
  }
  const prayerTextMatch = clean.match(/\[PRAYER_TEXT\]([\s\S]*?)\[\/PRAYER_TEXT\]/);
  if (prayerTextMatch) {
    try { markers.prayerText = JSON.parse(prayerTextMatch[1]); } catch {}
    clean = clean.replace(prayerTextMatch[0], '').trim();
  }
  const prayNowMetaMatch = clean.match(/\[PRAY_NOW_META\]([\s\S]*?)\[\/PRAY_NOW_META\]/);
  if (prayNowMetaMatch) {
    try { markers.prayNowMeta = JSON.parse(prayNowMetaMatch[1]); } catch {}
    clean = clean.replace(prayNowMetaMatch[0], '').trim();
  }
  const prayerRequestMatch = clean.match(/\[PRAYER_REQUEST\]([\s\S]*?)\[\/PRAYER_REQUEST\]/);
  if (prayerRequestMatch) {
    try { markers.prayerRequest = JSON.parse(prayerRequestMatch[1]); } catch {}
    clean = clean.replace(prayerRequestMatch[0], '').trim();
  }
  const bibleStudyRefMatch = clean.match(/\[BIBLE_STUDY_REF\]([\s\S]*?)\[\/BIBLE_STUDY_REF\]/);
  if (bibleStudyRefMatch) {
    try { markers.bibleStudyRef = JSON.parse(bibleStudyRefMatch[1]); } catch {}
    clean = clean.replace(bibleStudyRefMatch[0], '').trim();
  }
  return { clean, markers };
}
const REACTIONS = ['🙏','❤️','🔥','🙌'];
const EDIT_WINDOW_MS = 10 * 60 * 1000;

function timeAgo(d, t) {
  if (!d) return '';
  const m = Math.floor((Date.now()-new Date(d))/60000);
  if (m<1) return t('common.justNow');
  if (m<60) return t('common.mAgo', m);
  const h=Math.floor(m/60); if (h<24) return t('common.hAgo', h);
  return t('common.dAgo', Math.floor(h/24));
}
function getInitials(n='') { return n.split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2)||'?'; }


function BiblePlanCard({ meta }) {
  const unitWord  = meta.cadence === 'week' ? 'Week' : 'Day';
  const accentColor = meta.planColor || '#0d9488';
  return (
    <div className="growth-share-card bp-share-feed-card" style={{borderLeftColor: accentColor}}>
      <div className="growth-share-header">
        <span className="growth-share-badge bp-share-badge" style={{background: accentColor}}>📖 Bible Reading Plan</span>
        <span className="growth-share-plan">{meta.planTitle}</span>
      </div>
      <div className="growth-share-unit" style={{color: accentColor}}>
        {unitWord} {meta.dayNum}{meta.dayTitle ? ` · ${meta.dayTitle}` : ''}
      </div>
      {meta.verses && (
        <div className="growth-share-verse">{meta.verses}</div>
      )}
      {meta.qa?.filter(item => item.a).length > 0 && (
        <div className="growth-share-qa">
          {meta.qa.filter(item => item.a).map((item, i) => (
            <div key={i} className="growth-share-qa-item">
              {item.q && <p className="growth-share-question">{item.q}</p>}
              <p className="growth-share-answer">{item.a}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DailyRhythmCard({ meta }) {
  const accentColor = meta.programColor || '#E8751A';
  return (
    <div className="growth-share-card dr-share-feed-card" style={{borderLeftColor: accentColor}}>
      <div className="growth-share-header">
        <span className="growth-share-badge" style={{background: accentColor}}>
          <Sunrise size={11} strokeWidth={2.5} aria-hidden="true" /> Daily Rhythm
        </span>
        <span className="growth-share-plan">{meta.programTitle}</span>
      </div>
      <div className="growth-share-unit" style={{color: accentColor}}>
        Day {meta.dayNumber}{meta.theme ? ` · ${meta.theme}` : ''}
      </div>
      {meta.scriptureRef && (
        <div className="growth-share-verse">{meta.scriptureRef}</div>
      )}
      {meta.qa?.filter(item => item.a).length > 0 && (
        <div className="growth-share-qa">
          {meta.qa.filter(item => item.a).map((item, i) => (
            <div key={i} className="growth-share-qa-item">
              {item.q && <p className="growth-share-question">{item.q}</p>}
              <p className="growth-share-answer">{item.a}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Structured content display components ─────────────────────────────────────
function ScriptureCard({ reference, text }) {
  return (
    <div className="scripture-card">
      <p className="scripture-card-text">"{text}"</p>
      <p className="scripture-card-ref">— {reference}</p>
    </div>
  );
}

function PraisingDeclaration({ what }) {
  return (
    <div className="praising-declaration">
      <span className="praising-label">Praising</span>
      <span className="praising-text">{what}</span>
    </div>
  );
}

function SurrenderDeclaration({ what }) {
  return (
    <div className="surrender-declaration">
      <span className="surrender-label">Surrendering</span>
      <span className="surrender-text">{what}</span>
    </div>
  );
}

function PrayerTextBlock({ text }) {
  return (
    <div className="prayer-text-block">
      <p className="prayer-text-content">{text}</p>
    </div>
  );
}

const MOMENT_LABELS = {
  gratitude: 'Gratitude', intercession: 'Intercession', confession: 'Confession',
  praise: 'Praise', guidance: 'Guidance', comfort: 'Comfort', protection: 'Protection',
  healing: 'Healing', strength: 'Strength', other: 'Prayer',
};

function PrayNowMetaBadge({ meta }) {
  const momentLabel = MOMENT_LABELS[meta.moment] || 'Prayer';
  return (
    <div className="pray-now-meta-badge">
      <span className="pray-now-meta-moment">{momentLabel}</span>
      {meta.forWhom && meta.forWhom !== 'myself' && (
        <span className="pray-now-meta-for">· for {meta.forWhom}</span>
      )}
      {meta.prayerText && (
        <div className="pray-now-prayer-preview">{meta.prayerText}</div>
      )}
    </div>
  );
}

function PrayerRequestCard({ req }) {
  return (
    <div className={`prayer-request-card${req.isAnswered ? ' prayer-request-card--answered' : ''}`}>
      <div className="prayer-request-card-header">
        {req.isAnswered
          ? <CheckCircle2 size={13} strokeWidth={2.5} className="prayer-request-card-icon prayer-request-card-icon--answered"/>
          : <Heart size={13} strokeWidth={2.5} className="prayer-request-card-icon"/>}
        <span className="prayer-request-card-label">{req.isAnswered ? 'Answered Prayer' : 'Prayer Request'}</span>
        {req.requesterName && (
          <span className="prayer-request-card-from">from {req.requesterName}</span>
        )}
      </div>
      <p className="prayer-request-card-text">{req.title || req.text}</p>
      {req.title && req.text && (
        <p className="prayer-request-card-desc">{req.text}</p>
      )}
      {req.isAnswered && req.answeredNote && (
        <div className="prayer-request-card-testimony">
          <span className="prayer-request-card-testimony-label">How God answered</span>
          <p>{req.answeredNote}</p>
        </div>
      )}
      {!req.isAnswered && req.latestUpdate && (
        <div className="prayer-request-card-update">
          <MessageCircle size={11} className="prayer-request-card-update-icon"/>
          <span>{req.latestUpdate}</span>
        </div>
      )}
    </div>
  );
}

function BibleStudyRefCard({ studyRef }) {
  return (
    <div className="bible-study-ref-card">
      <BookOpen size={13} strokeWidth={2} className="bible-study-ref-icon"/>
      <span className="bible-study-ref-label">Bible Study</span>
      <span className="bible-study-ref-reference">{studyRef.reference}</span>
    </div>
  );
}

function RichTextLines({ text, onHashtagClick, onMentionClick }) {
  const lines = text.split('\n');
  return (
    <p className="post-content">
      {lines.map((line, li) => {
        const parts = line.split(/([@#][a-zA-Z0-9_.]+)/g);
        return (
          <React.Fragment key={li}>
            {parts.map((part, i) => {
              if (part.startsWith('@')) {
                const username = part.slice(1);
                return (
                  <span key={i} className="post-mention post-mention-link"
                    onClick={e => { e.stopPropagation(); onMentionClick && onMentionClick(username); }}>
                    {part}
                  </span>
                );
              }
              if (part.startsWith('#')) {
                return (
                  <button key={i} className="post-hashtag" onClick={e => { e.stopPropagation(); onHashtagClick && onHashtagClick(part.slice(1)); }}>
                    {part}
                  </button>
                );
              }
              return part;
            })}
            {li < lines.length - 1 && <br/>}
          </React.Fragment>
        );
      })}
    </p>
  );
}

function RichContent({ text, onHashtagClick, onMentionClick, groupId, postId, mediaUrl }) {
  if (!text) return null;
  const shareCard = renderShareCard(text, { groupId, postId, mediaUrl });
  if (shareCard) return shareCard;
  if (text.startsWith('DRMETA:')) {
    try { return <DailyRhythmCard meta={JSON.parse(text.slice(7))}/>; } catch {}
  }
  if (text.startsWith('BPMETA:')) {
    try {
      const meta = JSON.parse(text.slice(7));
      return <BiblePlanCard meta={meta}/>;
    } catch {}
  }

  // Parse new structured markers
  const { clean, markers } = parsePostContent(text);
  const hasMarkers = markers.scripture || markers.praising || markers.surrendering ||
                     markers.prayerText || markers.prayNowMeta || markers.prayerRequest || markers.bibleStudyRef;
  if (!hasMarkers) {
    // No structured markers — render plain rich text
    return <RichTextLines text={text} onHashtagClick={onHashtagClick} onMentionClick={onMentionClick}/>;
  }

  return (
    <>
      {markers.surrendering && <SurrenderDeclaration what={markers.surrendering.what}/>}
      {(markers.praising || markers.prayerRequest) && clean && <RichTextLines text={clean} onHashtagClick={onHashtagClick} onMentionClick={onMentionClick}/>}
      {markers.praising && <PraisingDeclaration what={markers.praising.what}/>}
      {markers.prayerRequest && <PrayerRequestCard req={markers.prayerRequest}/>}
      {!(markers.praising || markers.prayerRequest) && clean && <RichTextLines text={clean} onHashtagClick={onHashtagClick} onMentionClick={onMentionClick}/>}
      {markers.scripture && <ScriptureCard reference={markers.scripture.ref} text={markers.scripture.text}/>}
      {markers.prayerText && <PrayerTextBlock text={markers.prayerText.text}/>}
      {markers.prayNowMeta && <PrayNowMetaBadge meta={markers.prayNowMeta}/>}
      {markers.bibleStudyRef && <BibleStudyRefCard studyRef={markers.bibleStudyRef}/>}
    </>
  );
}

function useMentionAutocomplete(text, onSelect) {
  const [suggestions, setSuggestions]     = useState([]);
  const [mentionQuery, setMentionQuery]   = useState(null);
  const [resolvedMentions, setResolved]   = useState([]); // [{username, name, avatar}]
  const timerRef = useRef();

  // Keep resolvedMentions in sync with @usernames present in text
  useEffect(() => {
    const found = [...text.matchAll(/@([a-zA-Z0-9_.]+)/g)].map(m => m[1].toLowerCase());
    setResolved(prev => prev.filter(u => found.includes(u.username.toLowerCase())));
  }, [text]);

  useEffect(() => {
    const match = text.match(/@([a-zA-Z0-9_.]*)$/);
    if (match) {
      const q = match[1]; setMentionQuery(q);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(async () => {
        if (q.length < 1) { setSuggestions([]); return; }
        try {
          const d = await api.get(`/api/users?q=${encodeURIComponent(q)}&limit=5`);
          setSuggestions((d.users || []).filter(u => u.username));
        } catch(err) {
          console.error('Mention search failed:', err);
          setSuggestions([]);
        }
      }, 200);
    } else { setMentionQuery(null); setSuggestions([]); }
    return () => clearTimeout(timerRef.current);
  }, [text]);

  function selectUser(user) {
    onSelect(text.replace(/@([a-zA-Z0-9_.]*)$/, `@${user.username} `));
    setSuggestions([]); setMentionQuery(null);
    setResolved(prev => {
      if (prev.find(u => u.username === user.username)) return prev;
      return [...prev, { username: user.username, name: user.name, avatar: user.avatar }];
    });
  }
  return { suggestions, mentionQuery, selectUser, resolvedMentions };
}

function Lightbox({ urls, idx: initialIdx, onClose }) {
  const [idx, setIdx] = useState(initialIdx || 0);
  const count = urls?.length || 1;
  const src = Array.isArray(urls) ? urls[idx] : urls;
  const touchStartX = useRef(0);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [swiping, setSwiping] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    function fn(e) {
      if (e.key === 'Escape')      onClose();
      if (e.key === 'ArrowRight')  setIdx(i => Math.min(i + 1, count - 1));
      if (e.key === 'ArrowLeft')   setIdx(i => Math.max(i - 1, 0));
    }
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [onClose, count]);

  function onTouchStart(e) { touchStartX.current = e.touches[0].clientX; setSwiping(true); }
  function onTouchMove(e) { if (swiping) setSwipeOffset(e.touches[0].clientX - touchStartX.current); }
  function onTouchEnd() {
    if (Math.abs(swipeOffset) > 50) {
      if (swipeOffset < 0 && idx < count - 1) setIdx(i => i + 1);
      if (swipeOffset > 0 && idx > 0) setIdx(i => i - 1);
    }
    setSwipeOffset(0);
    setSwiping(false);
  }

  return ReactDOM.createPortal(
    <div className="lightbox-overlay" onClick={onClose}>
      <button className="lightbox-close" onClick={onClose}><X size={20}/></button>
      <img src={src} alt="" className="lightbox-media"
        onClick={e=>e.stopPropagation()}
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
        style={{ transform: `translateX(${swipeOffset}px)`, transition: swiping ? 'none' : 'transform 0.2s ease' }}
        draggable={false}/>
      {count > 1 && (
        <>
          <button className="lightbox-prev" onClick={e=>{e.stopPropagation();setIdx(i=>Math.max(i-1,0));}}
            disabled={idx===0}><ChevronLeft size={24}/></button>
          <button className="lightbox-next" onClick={e=>{e.stopPropagation();setIdx(i=>Math.min(i+1,count-1));}}
            disabled={idx===count-1}><ChevronRight size={24}/></button>
          <div className="lightbox-dots">
            {urls.map((_, i) => (
              <button key={i} className={`lightbox-dot${i===idx?' active':''}`}
                onClick={e=>{e.stopPropagation();setIdx(i);}}/>
            ))}
          </div>
          <div className="lightbox-counter">{idx+1} / {count}</div>
        </>
      )}
    </div>, document.body
  );
}

function EditForm({ post, onSave, onClose }) {
  const { t } = useTranslation();
  const [content, setContent] = useState(post.content);
  const [saving, setSaving]   = useState(false);
  async function save() {
    setSaving(true);
    try { await onSave(post.id, content); onClose(); }
    catch(err) { alert(err.message || t('feed.failedSave')); }
    finally { setSaving(false); }
  }
  return (
    <div className="edit-form">
      <textarea className="edit-textarea" value={content} onChange={e=>setContent(e.target.value)} rows={5}/>
      <div className="edit-actions">
        <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? <span className="spinner"/> : t('common.save')}</button>
        <button className="btn btn-outline" onClick={onClose}>{t('common.cancel')}</button>
      </div>
    </div>
  );
}

function RepostPreviewBody({ text, groupId, postId, mediaUrl, truncate = 120 }) {
  if (!text) return null;
  const card = renderShareCard(text, { groupId, postId, mediaUrl });
  if (card) return card;
  if (text.startsWith('DRMETA:')) {
    try { return <DailyRhythmCard meta={JSON.parse(text.slice(7))}/>; } catch {}
  }
  if (text.startsWith('BPMETA:')) {
    try { return <BiblePlanCard meta={JSON.parse(text.slice(7))}/>; } catch {}
  }
  const { clean } = parsePostContent(text);
  const display = clean || text;
  return <p className="repost-preview-text">{display.slice(0, truncate)}{display.length > truncate ? '…' : ''}</p>;
}

function RepostModal({ post, onRepost, onClose }) {
  const { t } = useTranslation();
  const [quote, setQuote] = useState('');
  const [loading, setLoading] = useState(false);
  async function submit() {
    setLoading(true);
    const ok = await onRepost(post.id, quote);
    setLoading(false);
    if (ok) onClose();
  }
  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title">{t('feed.repostTitle')}</span>
          <button className="modal-close" onClick={onClose}><X size={16}/></button>
        </div>
        <div className="repost-original-preview">
          <div className="repost-preview-author">{shortName(post.author?.name)}</div>
          <RepostPreviewBody text={post.content} groupId={post.groupId} postId={post.id} mediaUrl={post.mediaUrl}/>
        </div>
        <textarea className="create-textarea" placeholder={t('feed.addComment')} rows={3}
          value={quote} onChange={e=>setQuote(e.target.value)} style={{marginBottom:12}}/>
        <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
          <button className="btn btn-outline" onClick={onClose}>{t('feed.cancel')}</button>
          <button className="btn btn-primary" onClick={submit} disabled={loading}>
            {loading ? <span className="spinner"/> : <><Repeat2 size={15}/> {t('feed.repost')}</>}
          </button>
        </div>
      </div>
    </div>, document.body
  );
}

function PostCard({ post, currentDbUserId, onReact, onDelete, onEdit, onHashtagClick, onBookmark, onRepost }) {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [showComments, setShowComments] = useState(false);
  const [showMenu, setShowMenu]         = useState(false);
  const [showEdit, setShowEdit]         = useState(false);
  const [showRepost, setShowRepost]     = useState(false);
  const [showReport, setShowReport]     = useState(false);
  const [lightbox, setLightbox]         = useState(null);
  const [commentCount, setCommentCount] = useState(post.commentCount ?? post.comments?.length ?? 0);
  const [reactionSheet, setReactionSheet] = useState(null); // { filter: emoji|null }
  const menuRef = useRef();

  useEffect(() => { setCommentCount(post.commentCount ?? post.comments?.length ?? 0); }, [post.id]);
  useEffect(() => {
    const fn = e => { if(menuRef.current&&!menuRef.current.contains(e.target)) setShowMenu(false); };
    document.addEventListener('mousedown', fn); return () => document.removeEventListener('mousedown', fn);
  }, []);

  const tm         = TYPE_META[post.type] || TYPE_META.reflection;
  const tc         = TYPE_ICONS[post.type] || TYPE_ICONS.reflection;
  const TypeIcon   = tm.Icon;
  const typeLabel  = t(`feed.postTypes.${post.type}`) || post.type;
  const tagline    = t(`feed.postTypes.${tm.taglineKey}`) || '';
  const isOwn      = post.author?.id === currentDbUserId;
  const ageMs      = Date.now() - new Date(post.createdAt).getTime();
  const canEdit    = isOwn && ageMs <= EDIT_WINDOW_MS;
  const myReaction = (post.userReactions||{})[currentDbUserId];

  return (
    <div className="post-card card fade-up">
      {/* ── Type Banner ──────────────────────────────────────────── */}
      <div className={`post-type-banner post-type-banner--${tm.bannerClass}`}>
        <div className="post-type-banner-icon">
          <TypeIcon size={16} strokeWidth={2}/>
        </div>
        <span className="post-type-banner-label">{typeLabel}</span>
        {tagline && <span className="post-type-banner-tagline">{tagline}</span>}
        <div ref={menuRef} style={{position:'relative',flexShrink:0,marginLeft:'auto'}}>
          <button className="post-menu-btn post-menu-btn--banner" onClick={()=>setShowMenu(s=>!s)}>
            <MoreHorizontal size={17} strokeWidth={2}/>
          </button>
          {showMenu && (() => {
            const reactionTotal = Object.values(post.reactions || {}).reduce((s, n) => s + n, 0);
            return (
              <div className="post-menu-dropdown">
                {isOwn && reactionTotal > 0 && (
                  <button onClick={()=>{setShowMenu(false);setReactionSheet({filter:null});}}>
                    <Users size={14}/> {t('reactions.viewAll')} ({reactionTotal})
                  </button>
                )}
                {!isOwn && isAdmin && reactionTotal > 0 && (
                  <button onClick={()=>{setShowMenu(false);setReactionSheet({filter:null});}}>
                    <Users size={14}/> {t('reactions.viewAll')} ({reactionTotal})
                  </button>
                )}
                {isOwn && canEdit && <button onClick={()=>{setShowMenu(false);setShowEdit(true);}}><Pencil size={14}/> {t('common.edit')}</button>}
                {isOwn && !canEdit && <button disabled style={{opacity:0.4,cursor:'default'}}><Pencil size={14}/> {t('common.edit')} ({t('common.expired')})</button>}
                {isOwn && <button className="danger" onClick={()=>{setShowMenu(false);onDelete(post.id);}}><Trash2 size={14}/> {t('common.delete')}</button>}
                {!isOwn && <button onClick={()=>{setShowMenu(false);setShowReport(true);}}><Flag size={14}/> {t('feed.reportPost')}</button>}
              </div>
            );
          })()}
        </div>
        {showReport && <ReportModal contentType="post" contentId={post.id} onClose={()=>setShowReport(false)}/>}
        {reactionSheet && (
          <ReactionsSheet
            scope="post"
            id={post.id}
            initialFilter={reactionSheet.filter}
            initialCounts={post.reactions || {}}
            onClose={() => setReactionSheet(null)}
          />
        )}
      </div>

      {/* ── Author Row ───────────────────────────────────────────── */}
      <div className="post-header">
        <div className="post-avatar" onClick={()=>post.author?.id&&navigate(`/members/${post.author.id}`)}>
          {post.author?.avatar
            ? <img src={post.author.avatar} alt={post.author?.name} loading="lazy" style={{width:"100%",height:"100%",borderRadius:"50%",objectFit:"cover"}}/>
            : getInitials(post.author?.name)}
        </div>
        <div className="post-meta">
          <div className="post-author-row">
            <span className="post-author" onClick={()=>post.author?.id&&navigate(`/members/${post.author.id}`)}>
              {shortName(post.author?.name)}
            </span>
            {post.author?.username && <span className="post-username">@{post.author.username}</span>}
          </div>
          <div className="post-meta-row">
            <span className="post-time">{timeAgo(post.createdAt, t)}</span>
            {post.group && (
              <span className="post-community-tag" onClick={e=>{e.stopPropagation();navigate(`/communities/${post.group.id}`);}}>
                · {post.group.name}
              </span>
            )}
            {canEdit && <span className="post-editable">· {t('common.editable') || 'editable'}</span>}
          </div>
        </div>
      </div>

      <RichContent text={post.content} groupId={post.groupId} postId={post.id} mediaUrl={post.mediaUrl} onHashtagClick={onHashtagClick} onMentionClick={async (username) => {
        try {
          const d = await api.get(`/api/users?q=${encodeURIComponent(username)}&limit=5`);
          const match = (d.users||[]).find(u => u.username?.toLowerCase() === username.toLowerCase());
          if (match) navigate(`/members/${match.id}`);
        } catch {}
      }}/>

      {post.hashtags && post.hashtags.length > 0 && (
        <div className="post-hashtags">
          {post.hashtags.map(tag => (
            <button key={tag} className="post-hashtag-chip" onClick={() => onHashtagClick && onHashtagClick(tag)}>
              <Hash size={11}/>{tag}
            </button>
          ))}
        </div>
      )}

      {post.repostOf && (
        <div className="repost-embed">
          <div className="repost-embed-header">
            <div className="repost-embed-avatar">{getInitials(post.repostOf.author?.name)}</div>
            <span className="repost-embed-author">{shortName(post.repostOf.author?.name)}</span>
            {post.repostOf.author?.username && <span className="repost-embed-handle">@{post.repostOf.author.username}</span>}
          </div>
          <RepostPreviewBody text={post.repostOf.content} groupId={post.repostOf.groupId} postId={post.repostOf.id} mediaUrl={post.repostOf.mediaUrl} truncate={240}/>
          {post.repostOf.mediaUrl && (
            <img src={post.repostOf.mediaUrl} alt="" className="repost-embed-img" loading="lazy"/>
          )}
        </div>
      )}

      {(() => {
        // Share-card posts (auto-generated [WELL_PUBLISHED], [BRP_PUBLISHED], etc.)
        // embed their own hero image inside the card — skip the duplicate media grid.
        const isShareCardPost = typeof post.content === 'string' && /^\[[A-Z_]+\]/.test(post.content);
        if (isShareCardPost) return null;
        const urls = (post.mediaUrls?.length ? post.mediaUrls : post.mediaUrl ? [post.mediaUrl] : []);
        const isVideo = post.mediaType === 'video';
        if (!urls.length) return null;
        if (isVideo) return (
          <div className="post-media-full">
            <video src={urls[0]} controls className="post-media-full-el"/>
          </div>
        );
        const openLightbox = idx => setLightbox({ urls, idx });
        const count = urls.length;
        return (
          <div className={`post-media-grid count-${Math.min(count,4)}`}>
            {urls.slice(0, 4).map((url, i) => (
              <div key={i} className="post-media-grid-item" onClick={()=>openLightbox(i)}>
                <img src={url} alt="" className="post-media-grid-img" loading="lazy"/>
                {i === 3 && count > 4 && (
                  <div className="post-media-grid-more">+{count - 4}</div>
                )}
              </div>
            ))}
          </div>
        );
      })()}

      <div className="post-footer">
        <div className="post-reactions">
          {REACTIONS.map(emoji => {
            const count = (post.reactions||{})[emoji]||0;
            const mine  = myReaction===emoji;
            return (
              <button key={emoji} className={`reaction-btn${mine?' mine':''}`} onClick={()=>onReact(post.id,emoji)}>
                {emoji}{count>0&&<span>{count}</span>}
              </button>
            );
          })}
        </div>
        <div className="post-footer-actions">
          <button className={`comment-toggle-btn${showComments?' open':''}`} onClick={()=>setShowComments(s=>!s)}>
            <MessageCircle size={15} strokeWidth={2}/>
            <span>{commentCount}</span>
          </button>
          {!isOwn && onRepost && (
            <button className="post-action-btn" onClick={()=>setShowRepost(true)} title="Repost">
              <Repeat2 size={15} strokeWidth={2}/>
            </button>
          )}
          {onBookmark && (
            <button className={`post-action-btn${post.bookmarked?' bookmarked':''}`}
              onClick={()=>onBookmark(post.id)} title={post.bookmarked ? 'Remove bookmark' : 'Save'}>
              {post.bookmarked
                ? <BookmarkCheck size={15} strokeWidth={2}/>
                : <Bookmark size={15} strokeWidth={2}/>}
            </button>
          )}
        </div>
      </div>
      {showRepost && <RepostModal post={post} onRepost={onRepost} onClose={()=>setShowRepost(false)}/>}

      {showComments && (
        <CommentSection targetId={post.id} initialComments={post.comments||[]}
          onCountChange={delta => setCommentCount(c => Math.max(0, c + delta))}/>
      )}
      {lightbox && <Lightbox urls={lightbox.urls} idx={lightbox.idx} onClose={()=>setLightbox(null)}/>}

      {showEdit && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowEdit(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{t('feed.editPost')}</span>
              <button className="modal-close" onClick={()=>setShowEdit(false)}><X size={16}/></button>
            </div>
            <EditForm post={post} onSave={onEdit} onClose={()=>setShowEdit(false)}/>
          </div>
        </div>, document.body
      )}
    </div>
  );
}

export {
  PostCard,
  PostCard as default,
  RichContent,
  RichTextLines,
  TYPE_META,
  TYPE_ICONS,
  REACTIONS,
  EDIT_WINDOW_MS,
  MOMENT_LABELS,
  parsePostContent,
  timeAgo,
  getInitials,
  useMentionAutocomplete,
  Lightbox,
  CommentSection,
  EditForm,
  RepostModal,
  BiblePlanCard,
  DailyRhythmCard,
};
